// Display formatters for Patient Inquiry records (PRD 12 §4.3).
// Wire format → UI text. Name masking is FE-only; the BE returns raw values.

import { maskName } from '@/features/patients/utils/format-patient';

import type {
  PatientInquiryGender,
  PatientInquiryListItem,
} from '../types/patient-inquiry.types';

// PRD §4.3: title column truncates at 60 chars with an ellipsis.
const TITLE_MAX_CHARS = 60;

export function truncateTitle(title: string, max: number = TITLE_MAX_CHARS): string {
  if (title.length <= max) return title;
  return `${title.slice(0, max)}...`;
}

// PRD §4.3: dates render as YYYY.MM.DD. Wire is YYYY-MM-DD (date-only).
// Mirrors patients/format-patient but kept local to avoid a cross-feature
// coupling on a formatter that may diverge.
export function formatBirthDate(value: string | null): string {
  if (!value) return '-';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value.replace(/-/g, '.');
  }
  return value;
}

// PRD §4.3: inquiry/reply timestamps render as YYYY.MM.DD HH:MM (local time).
// Wire is ISO-8601. Falls back to the raw string on an unparseable value.
export function formatInquiredAt(value: string): string {
  // Already in display form (e.g. legacy "2026.03.19 11:20") → pass through.
  if (/^\d{4}\.\d{2}\.\d{2}/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(
    d.getDate(),
  )} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// PRD §4.3: inquirer UID renders as "U-{id}". When the patient lookup did not
// resolve to an account (BE emits no id), the wire value can be an empty/"U-null"
// placeholder — never surface that verbatim; show "-" instead. Pairs with BE-6.
export function formatInquirerUid(uid: string | null | undefined): string {
  if (!uid || /-null$/i.test(uid)) return '-';
  return uid;
}

// PRD §3.1 / §4.3: inquirer cell = "{masked name} ({gender}/{birthDate})".
// Precedence (PRD §3.1 / §8.2 / §8.3): a failed patient lookup shows the
// patient-info error (row stays visible); a genuine withdrawal shows the
// withdrawn label; otherwise the masked identity. The caller supplies the
// localized labels so this stays i18n-agnostic.
export function formatInquirer(
  item: Pick<
    PatientInquiryListItem,
    'inquirerName' | 'gender' | 'birthDate' | 'isWithdrawn' | 'patientLookupFailed'
  >,
  genderLabels: Record<PatientInquiryGender, string>,
  withdrawnLabel: string,
  lookupFailedLabel: string,
): string {
  if (item.patientLookupFailed) return lookupFailedLabel;
  if (item.isWithdrawn) return withdrawnLabel;
  const masked = maskName(item.inquirerName);
  const gender = item.gender ? genderLabels[item.gender] : '';
  const dob = formatBirthDate(item.birthDate);
  return `${masked} (${gender}/${dob})`;
}
